// ===== Interfaces =====

import { ITranscriptionWord } from '../interfaces';

// ===== Services =====

import getTranscriptionWordTimestampMs from './getTranscriptionWordTimestampMs';

interface IResolveActiveTranscriptionWordIndexArgs {
    transcriptWords: ITranscriptionWord[],
    currentTimeMs: number,
    isPlaying: boolean,
    upcomingWordLeadMs?: number,
    trailingWordHoldMs?: number,
}

/**
 * Resolves the transcript word that currently owns the playback head so
 * read-along highlights only update when the spoken word actually changes.
 *
 * Long post-audio transcripts can contain tens of thousands of timed words, so
 * this uses a boundary-safe binary search instead of a linear scan on every
 * playback clock tick.
 */
const resolveActiveTranscriptionWordIndex = ({
    transcriptWords,
    currentTimeMs,
    isPlaying,
    upcomingWordLeadMs = 0,
    trailingWordHoldMs = 0,
}: IResolveActiveTranscriptionWordIndexArgs): number | null => {
    if (
        transcriptWords.length === 0
        || (!isPlaying && currentTimeMs <= 0)
    ) {
        return null;
    }

    let left = 0;
    let right = transcriptWords.length - 1;
    let candidateIndex: number | null = null;

    while (left <= right) {
        const middle = Math.floor((left + right) / 2);
        const word = transcriptWords[middle];
        const endTimeMs = getTranscriptionWordTimestampMs({
            word,
            edge: 'end',
        });

        if (currentTimeMs <= endTimeMs) {
            candidateIndex = middle;
            right = middle - 1;
        } else {
            left = middle + 1;
        }
    }

    if (candidateIndex == null) {
        return null;
    }

    for (let index = candidateIndex; index < transcriptWords.length; index += 1) {
        const word = transcriptWords[index];
        const startTimeMs = getTranscriptionWordTimestampMs({
            word,
            edge: 'start',
        });
        const endTimeMs = getTranscriptionWordTimestampMs({
            word,
            edge: 'end',
        });

        if (currentTimeMs >= startTimeMs && currentTimeMs <= endTimeMs) {
            const nextWord = transcriptWords[index + 1];
            if (nextWord && upcomingWordLeadMs > 0) {
                const nextWordStartTimeMs = getTranscriptionWordTimestampMs({
                    word: nextWord,
                    edge: 'start',
                });
                const timeUntilNextWordStartsMs = nextWordStartTimeMs - currentTimeMs;

                if (
                    timeUntilNextWordStartsMs > 0
                    && timeUntilNextWordStartsMs <= upcomingWordLeadMs
                ) {
                    return index + 1;
                }
            }

            return index;
        }

        if (startTimeMs > currentTimeMs) {
            if ((startTimeMs - currentTimeMs) <= upcomingWordLeadMs) {
                return index;
            }

            const previousWordIndex = index - 1;
            if (previousWordIndex >= 0 && trailingWordHoldMs > 0) {
                const previousWordEndTimeMs = getTranscriptionWordTimestampMs({
                    word: transcriptWords[previousWordIndex],
                    edge: 'end',
                });
                const timeSincePreviousWordEndedMs = currentTimeMs - previousWordEndTimeMs;

                if (
                    timeSincePreviousWordEndedMs >= 0
                    && timeSincePreviousWordEndedMs <= trailingWordHoldMs
                ) {
                    return previousWordIndex;
                }
            }

            return null;
        }
    }

    return null;
};

export default resolveActiveTranscriptionWordIndex;
